import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import {
  RefreshCw, Table2, UserCheck, UserX, ArrowLeft, Users, DoorOpen,
  Ticket, Clock, CheckCircle2, Loader2, Receipt, ExternalLink,
  Star, AlertTriangle,
} from "lucide-react";

const STATUS_COLORS = {
  checked_in:       "text-emerald-500 border-emerald-500/40 bg-emerald-500/5",
  confirmed:        "text-indigo-500 border-indigo-500/40 bg-indigo-500/5",
  seated:           "text-foreground/60 border-foreground/20 bg-foreground/5",
  awaiting_deposit: "text-amber-500 border-amber-500/40 bg-amber-500/5",
  no_show:          "text-red-500 border-red-500/40 bg-red-500/5",
  cancelled:        "text-red-500 border-red-500/40 bg-red-500/5",
  completed:        "text-foreground/40 border-foreground/20 bg-foreground/[0.02]",
};

export default function HostQuickViewPage() {
  const { branchId, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [arrivals, setArrivals] = useState([]);
  const [tables, setTables] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [tablePicker, setTablePicker] = useState(null); // {reservation, heads}
  const [pkgPicker, setPkgPicker] = useState(null); // {reservation}

  const load = async () => {
    if (!branchId) return;
    try {
      const [{ data: arr }, { data: tbls }, { data: pks }] = await Promise.all([
        api.get("/quick/tonight-arrivals", { params: { branch_id: branchId } }),
        api.get("/tables", { params: { branch_id: branchId } }),
        api.get("/vip-packages", { params: { active_only: true } }).catch(() => ({ data: [] })),
      ]);
      setArrivals(arr);
      setTables(tbls);
      setPackages(pks);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const iv = setInterval(load, 8000); return () => clearInterval(iv); }, [branchId]);

  const active = useMemo(() => arrivals.filter((r) => ["checked_in", "confirmed", "seated"].includes(r.status)), [arrivals]);
  const inactive = useMemo(() => arrivals.filter((r) => !["checked_in", "confirmed", "seated"].includes(r.status)), [arrivals]);

  const seat = async (rid, tableId, heads, ignoreCap = false) => {
    setBusyId(rid);
    try {
      const { data } = await api.post(`/quick/reservations/${rid}/arrive-and-seat`,
                                        { table_id: tableId, actual_head_count: heads, ignore_capacity: ignoreCap });
      toast.success(data.already_seated ? "Already seated" : "Seated");
      setTablePicker(null); load();
    } catch (e) {
      const d = e.response?.data?.detail || "";
      if (typeof d === "string" && d.includes("capacity")) {
        if (window.confirm(`${d}\n\nProceed anyway?`)) return seat(rid, tableId, heads, true);
      } else { toast.error(formatApiError(d)); }
    } finally { setBusyId(null); }
  };

  const markNoShow = async (rid) => {
    const reason = window.prompt("Mark as NO-SHOW.\n\nReason (required):");
    if (!reason || !reason.trim()) return;
    setBusyId(rid);
    try {
      await api.post(`/reservations/${rid}/status`, { status: "no_show", reason: reason.trim() });
      toast.success("Marked no-show"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const openPosTab = async (r) => {
    setBusyId(r.id);
    try {
      const { data } = await api.post(`/quick/reservations/${r.id}/open-pos-tab`, {});
      const order = data.order;
      toast.success(data.already_open
        ? `Tab already open · ${order.order_number}`
        : `Tab opened · ${order.order_number}`);
      // If the user has POS permissions, jump straight into the terminal.
      if (hasPerm && hasPerm("order.create")) {
        navigate(`/pos/terminal?order=${order.id}`);
      } else {
        load();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const goToPos = (orderId) => navigate(`/pos/terminal?order=${orderId}`);

  const attachPackage = async (rid, packageId) => {
    setBusyId(rid);
    try {
      await api.post(`/reservations/${rid}/vip-package`, { package_id: packageId });
      toast.success("VIP package attached");
      setPkgPicker(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const detachPackage = async (rid) => {
    if (!window.confirm("Remove VIP package from this reservation?")) return;
    setBusyId(rid);
    try {
      await api.delete(`/reservations/${rid}/vip-package`);
      toast.success("VIP package removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  if (!branchId) return (
    <div className="p-14 max-w-4xl mx-auto">
      <div className="section-eyebrow">Host</div>
      <h1 className="mt-4 display-heading text-3xl ink">Select a branch first</h1>
    </div>
  );
  if (!hasPerm("reservation.manage")) return (
    <div className="p-14 ink-subtle text-sm">You don't have permission to view reservations.</div>
  );

  return (
    <div className="min-h-full" data-testid="host-page">
      <div className="max-w-5xl mx-auto px-6 py-8 lg:py-10">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <Link to="/" className="tiny-label ink-subtle hover:ink flex items-center gap-1"><ArrowLeft size={12}/> All modules</Link>
            <div className="mt-3 section-eyebrow">Host · Tonight</div>
            <h1 className="mt-1 display-heading text-3xl ink">Tonight's Arrivals</h1>
          </div>
          <button onClick={load} data-testid="host-refresh"
                  className="btn-pill btn-pill-outline h-10">
            <RefreshCw size={13}/> Refresh
          </button>
        </div>

        {loading && arrivals.length === 0 && (
          <div className="text-center py-14 ink-subtle text-sm"><Loader2 className="animate-spin inline mr-2" size={14}/>Loading…</div>
        )}

        {!loading && active.length === 0 && (
          <div className="text-center py-14 border hairline-soft rounded-md ink-subtle" data-testid="host-empty">
            No active reservations for tonight yet.
          </div>
        )}

        <div className="space-y-3">
          {active.map((r) => (
            <ReservationCard key={r.id} r={r} tables={tables}
                              busy={busyId === r.id}
                              canOpenTab={hasPerm && hasPerm("reservation.open_pos_tab")}
                              onAssign={() => setTablePicker({ reservation: r, heads: r.actual_head_count ?? r.guest_count })}
                              onNoShow={() => markNoShow(r.id)}
                              onOpenPosTab={() => openPosTab(r)}
                              onGoToPos={() => goToPos(r.pos_order_id)}
                              onAttachPkg={() => setPkgPicker({ reservation: r })}
                              onDetachPkg={() => detachPackage(r.id)}
                              onSeatNow={r.table_id ? () => seat(r.id, r.table_id, r.actual_head_count ?? r.guest_count) : null}/>
          ))}
        </div>

        {inactive.length > 0 && (
          <details className="mt-10" data-testid="host-inactive-section">
            <summary className="tiny-label cursor-pointer ink-subtle hover:ink">
              Show {inactive.length} inactive · completed / cancelled / no-show
            </summary>
            <div className="mt-4 space-y-2">
              {inactive.map((r) => (
                <div key={r.id} className="opacity-60 p-3 border hairline-soft rounded-md text-xs flex items-center justify-between" data-testid={`host-inactive-${r.id}`}>
                  <div>{r.customer_name} · <span className="mono">{r.confirmation_code}</span></div>
                  <div className={`text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[r.status] || ""}`}>{r.status}</div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {tablePicker && (
        <TablePickerModal
          tables={tables} reservation={tablePicker.reservation} heads={tablePicker.heads}
          onCancel={() => setTablePicker(null)}
          onPick={(tid, headsFinal) => seat(tablePicker.reservation.id, tid, headsFinal)}/>
      )}
      {pkgPicker && (
        <PackagePickerModal
          packages={packages} reservation={pkgPicker.reservation}
          onCancel={() => setPkgPicker(null)}
          onPick={(pid) => attachPackage(pkgPicker.reservation.id, pid)}/>
      )}
    </div>
  );
}


function ReservationCard({ r, tables, busy, onAssign, onNoShow, onSeatNow,
                            onOpenPosTab, onGoToPos, canOpenTab,
                            onAttachPkg, onDetachPkg }) {
  const table = tables.find((t) => t.id === r.table_id);
  const snap = r.vip_package_snapshot;
  const depositPaid = Number(r.deposit_paid || 0);
  const requiredDep = snap ? Number(snap.required_deposit || 0) : 0;
  const depositShort = snap && requiredDep > depositPaid + 0.01
    ? Number((requiredDep - depositPaid).toFixed(2))
    : 0;
  const tableIsVip = table && (table.vip || table.table_type === "vip");
  const vipOnlyBlocking = snap && snap.vip_only && r.table_id && !tableIsVip;
  return (
    <div className="border hairline surface-card rounded-lg p-5" data-testid={`host-res-card-${r.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="display-heading text-2xl ink truncate" data-testid={`host-res-name-${r.id}`}>{r.customer_name}</div>
            <span className={`text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[r.status] || ""}`}
                  data-testid={`host-res-status-${r.id}`}>{r.status.replace("_", " ")}</span>
            {snap && (
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border text-amber-500 border-amber-500/40 bg-amber-500/5 inline-flex items-center gap-1"
                    data-testid={`host-res-vip-badge-${r.id}`}>
                <Star size={10}/> {snap.code}
              </span>
            )}
            {r.pos_order_id && (
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border text-violet-500 border-violet-500/40 bg-violet-500/5"
                    data-testid={`host-res-tab-open-${r.id}`}>tab open</span>
            )}
          </div>
          <div className="mt-1 mono text-xs ink-subtle">{r.confirmation_code}</div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-xs">
            <F k="Reservation" v={r.starts_at ? new Date(r.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}/>
            <F k="Expected" v={`${r.guest_count} pax`}/>
            <F k="Actual" v={r.actual_head_count != null ? `${r.actual_head_count} pax` : "—"} highlight={r.actual_head_count != null}/>
            <F k="Table" v={table ? `${table.name || table.id.slice(-6)} · cap ${table.capacity || "?"}` : "—"}/>
            <F k="Deposit" v={r.deposit_paid ? peso(r.deposit_paid) : "—"}/>
            {snap && <F k="Min spend" v={peso(snap.minimum_spend)} highlight/>}
            {r.promoter_name && <F k="Promoter" v={r.promoter_name}/>}
            {r.checked_in_at && <F k="Checked in" v={new Date(r.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} highlight/>}
          </div>
          {(depositShort > 0 || vipOnlyBlocking) && (
            <div className="mt-3 p-2.5 rounded-md border border-amber-500/40 bg-amber-500/5 text-xs text-amber-500 flex items-start gap-2"
                  data-testid={`host-res-warning-${r.id}`}>
              <AlertTriangle size={13} className="shrink-0 mt-0.5"/>
              <div>
                {depositShort > 0 && (
                  <div>Deposit short by <span className="font-bold">{peso(depositShort)}</span> · required {peso(requiredDep)}</div>
                )}
                {vipOnlyBlocking && (
                  <div>VIP-only package — current table isn't VIP</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {r.status !== "seated" && (
        <div className="mt-5 flex flex-wrap gap-2">
          <button onClick={onAssign} disabled={busy}
                  data-testid={`host-assign-${r.id}`}
                  className="btn-pill btn-pill-outline h-11 disabled:opacity-40">
            <Table2 size={13}/> {r.table_id ? "Change table" : "Assign table"}
          </button>
          {!r.pos_order_id && (
            snap ? (
              <button onClick={onDetachPkg} disabled={busy}
                       data-testid={`host-detach-pkg-${r.id}`}
                       className="btn-pill btn-pill-outline h-11 disabled:opacity-40">
                <Star size={13}/> Remove package
              </button>
            ) : (
              <button onClick={onAttachPkg} disabled={busy}
                       data-testid={`host-attach-pkg-${r.id}`}
                       className="btn-pill btn-pill-outline h-11 disabled:opacity-40">
                <Star size={13}/> Attach package
              </button>
            )
          )}
          {onSeatNow && r.table_id && (
            <button onClick={onSeatNow} disabled={busy}
                    data-testid={`host-seat-${r.id}`}
                    className="btn-pill btn-pill-solid h-11 text-sm disabled:opacity-40">
              <UserCheck size={13}/> {busy ? "Seating…" : "Arrive & Seat"}
            </button>
          )}
          {r.status === "confirmed" && (
            <button onClick={onNoShow} disabled={busy}
                    data-testid={`host-noshow-${r.id}`}
                    className="btn-pill btn-pill-outline h-11 text-red-500 border-red-500/40 hover:bg-red-500/5 disabled:opacity-40">
              <UserX size={13}/> Mark no-show
            </button>
          )}
        </div>
      )}
      {r.status === "seated" && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {r.seated_at && (
            <div className="tiny-label text-emerald-500 flex items-center gap-1">
              <CheckCircle2 size={12}/> Seated at {new Date(r.seated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
          {r.pos_order_id ? (
            <button onClick={onGoToPos} disabled={busy}
                    data-testid={`host-goto-pos-${r.id}`}
                    className="btn-pill btn-pill-solid h-11 text-sm disabled:opacity-40">
              <ExternalLink size={13}/> OPEN POS
            </button>
          ) : canOpenTab ? (
            <button onClick={onOpenPosTab}
                    disabled={busy || depositShort > 0 || vipOnlyBlocking}
                    data-testid={`host-open-tab-${r.id}`}
                    title={depositShort > 0 ? "Deposit shortfall — collect the remainder first"
                           : vipOnlyBlocking ? "VIP-only package requires a VIP table" : ""}
                    className="btn-pill btn-pill-solid h-11 text-sm disabled:opacity-40">
              <Receipt size={13}/> {busy ? "Opening…" : "OPEN POS TAB"}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}


function PackagePickerModal({ packages, reservation, onCancel, onPick }) {
  const [selected, setSelected] = useState("");
  return (
    <div className="fixed inset-0 z-40 flex items-end md:items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-xl surface-card rounded-lg border hairline p-6 max-h-[85vh] overflow-y-auto"
            data-testid="host-pkg-picker">
        <div className="flex justify-between items-start gap-4">
          <div>
            <div className="section-eyebrow">Attach VIP package</div>
            <div className="display-heading text-2xl ink mt-2">{reservation.customer_name}</div>
            <div className="mt-1 tiny-label ink-subtle">{reservation.guest_count} pax · deposit {peso(reservation.deposit_paid || 0)}</div>
          </div>
          <button onClick={onCancel} className="ink-subtle hover:ink">✕</button>
        </div>
        <div className="mt-5 space-y-2">
          {packages.length === 0 && (
            <div className="py-6 text-center ink-subtle text-xs">
              No active VIP packages configured. Add one in Reservations → VIP Packages.
            </div>
          )}
          {packages.map((p) => {
            const heads = reservation.actual_head_count ?? reservation.guest_count ?? 1;
            const overLimit = p.guest_limit > 0 && heads > p.guest_limit;
            return (
              <button key={p.id}
                       onClick={() => !overLimit && setSelected(p.id)}
                       disabled={overLimit}
                       data-testid={`host-pkg-option-${p.code}`}
                       className={`w-full text-left p-3 rounded-md border transition-colors ${selected === p.id ? "border-foreground bg-foreground/5" : "hairline hover:bg-foreground/5"} ${overLimit ? "opacity-40 cursor-not-allowed" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="mono text-xs ink-subtle">{p.code}</span>
                      {p.vip_only && <span className="text-[9px] uppercase tracking-widest text-amber-500">VIP only</span>}
                    </div>
                    <div className="mt-1 text-sm font-semibold ink">{p.name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="mono text-sm ink font-bold">{peso(p.minimum_spend)}</div>
                    <div className="tiny-label">min · dep {peso(p.required_deposit)}</div>
                  </div>
                </div>
                {overLimit && (
                  <div className="mt-2 text-xs text-amber-500">
                    Party of {heads} exceeds limit of {p.guest_limit}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onCancel} className="btn-pill btn-pill-outline h-11">Cancel</button>
          <button onClick={() => onPick(selected)} disabled={!selected}
                  data-testid="host-pkg-picker-confirm"
                  className="btn-pill btn-pill-solid h-11 disabled:opacity-40">
            <Star size={13}/> Attach
          </button>
        </div>
      </div>
    </div>
  );
}


function TablePickerModal({ tables, reservation, heads, onCancel, onPick }) {
  const [selected, setSelected] = useState(reservation.table_id || "");
  const [finalHeads, setFinalHeads] = useState(heads);
  const available = tables.filter((t) => {
    if (t.status === "occupied" && t.current_reservation_id !== reservation.id) return false;
    if (["out_of_service", "blocked", "inactive"].includes(t.status)) return false;
    if (t.status === "reserved" && t.current_reservation_id && t.current_reservation_id !== reservation.id) return false;
    return true;
  });
  const sorted = [...available].sort((a, b) => {
    // Prefer tables that fit
    const aFit = (a.capacity || 99) >= finalHeads ? 0 : 1;
    const bFit = (b.capacity || 99) >= finalHeads ? 0 : 1;
    if (aFit !== bFit) return aFit - bFit;
    return (a.capacity || 0) - (b.capacity || 0);
  });
  return (
    <div className="fixed inset-0 z-40 flex items-end md:items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl surface-card rounded-lg border hairline p-6 max-h-[85vh] overflow-y-auto"
            data-testid="host-table-picker">
        <div className="flex justify-between items-start gap-4">
          <div>
            <div className="section-eyebrow">Assign table for</div>
            <div className="display-heading text-2xl ink mt-2">{reservation.customer_name}</div>
          </div>
          <button onClick={onCancel} className="ink-subtle hover:ink">✕</button>
        </div>
        <label className="block mt-5">
          <div className="tiny-label">Actual heads arriving</div>
          <input type="number" min={1} value={finalHeads}
                 onChange={(e) => setFinalHeads(parseInt(e.target.value || 1))}
                 data-testid="host-picker-heads"
                 className="mt-2 h-12 w-full rounded-md border hairline surface-2 px-4 mono ink text-xl font-bold text-center"/>
        </label>
        <div className="mt-5 section-eyebrow mb-2">Available tables ({sorted.length})</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
          {sorted.map((t) => {
            const fits = (t.capacity || 99) >= finalHeads;
            return (
              <button key={t.id}
                      onClick={() => setSelected(t.id)}
                      data-testid={`host-picker-table-${t.id}`}
                      className={`p-3 rounded-md border text-left transition-colors ${selected === t.id ? "border-foreground bg-foreground/5" : "hairline hover:bg-foreground/5"} ${!fits ? "opacity-70" : ""}`}>
                <div className="text-sm font-semibold ink">{t.name || t.id.slice(-6)}</div>
                <div className="mt-1 tiny-label ink-subtle">cap {t.capacity || "?"} · {t.status}</div>
                {!fits && <div className="tiny-label text-amber-500 mt-1">Below actual heads</div>}
              </button>
            );
          })}
          {sorted.length === 0 && <div className="col-span-3 py-6 text-center ink-subtle text-xs">No suitable tables. Free one first.</div>}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onCancel} className="btn-pill btn-pill-outline h-11">Cancel</button>
          <button onClick={() => onPick(selected, finalHeads)}
                  disabled={!selected}
                  data-testid="host-picker-confirm"
                  className="btn-pill btn-pill-solid h-11 disabled:opacity-40">
            <UserCheck size={13}/> Arrive & Seat
          </button>
        </div>
      </div>
    </div>
  );
}


function F({ k, v, highlight }) {
  return (
    <div>
      <div className="tiny-label">{k}</div>
      <div className={`mono text-sm ${highlight ? "ink font-bold" : "ink"}`}>{v ?? "—"}</div>
    </div>
  );
}
